/**
 * Phase 6a.23 polish — Finding AI.
 *
 * Derive the list of month-start dates the backend requires for the
 * `historicalRates` array of an FX contract (IAS21_MONETARY approach).
 *
 * The backend pins a re-translation event to every reporting date in
 * the lease lifetime. Reporting dates anchor to the contract's
 * commencement day-of-month and step forward in 1-month increments,
 * for `leaseTermMonths` periods after commencement. The commencement
 * date itself is covered by `commencementSpotRate`, so this helper
 * starts at commencement + 1 month and emits N entries.
 *
 * Browser-sanity discovery (2026-05-16): the auditor was entering a
 * single year-end rate; backend returned 400 because every month-start
 * needs a rate. The UI now offers an "Aylık Üret" button wired to
 * this helper so the auditor can populate the full series with one
 * click and only fill in the rates.
 *
 * Day-of-month logic: mirrors `generatePaymentDates` — anchor to
 * commencement day, clamp to the last day of shorter months
 * (e.g. Jan 31 → Feb 28/29).
 */

export interface GenerateHistoricalRateDatesInput {
  commencementDate: string; // ISO YYYY-MM-DD
  leaseTermMonths: number;
  /**
   * BB4 / BI — optional upper bound. When supplied, month-start dates
   * strictly after this date are not emitted: backend substitutes the
   * engagement-level closing rate for proforma periods so prompting
   * the auditor for rates beyond the audit window is pure noise. The
   * manual "+ Satır Ekle" button remains for any caller that wants
   * to enter forward rates anyway.
   */
  auditPeriodEnd?: string;
}

export function generateHistoricalRateDates(
  input: GenerateHistoricalRateDatesInput,
): string[] {
  const { commencementDate, leaseTermMonths } = input;
  if (!commencementDate || !/^\d{4}-\d{2}-\d{2}$/.test(commencementDate)) {
    return [];
  }
  if (
    !Number.isInteger(leaseTermMonths) ||
    leaseTermMonths < 1 ||
    leaseTermMonths > 1200
  ) {
    return [];
  }

  const upperBound =
    input.auditPeriodEnd &&
    /^\d{4}-\d{2}-\d{2}$/.test(input.auditPeriodEnd)
      ? input.auditPeriodEnd
      : null;
  if (upperBound !== null && commencementDate > upperBound) {
    return [];
  }

  const [yStr, mStr, dStr] = commencementDate.split("-");
  const startY = Number(yStr);
  const startM = Number(mStr) - 1; // JS months are 0-indexed
  const anchorDay = Number(dStr);

  const out: string[] = [];
  for (let i = 1; i <= leaseTermMonths; i++) {
    const targetY = startY + Math.floor((startM + i) / 12);
    const targetM = (startM + i) % 12;
    // End-of-month clamping — `new Date(Y, M+1, 0)` returns the last
    // day of month M (UTC). `Math.min(anchorDay, daysInTargetMonth)`
    // gives the closest valid day-of-month.
    const daysInTargetMonth = new Date(
      Date.UTC(targetY, targetM + 1, 0),
    ).getUTCDate();
    const day = Math.min(anchorDay, daysInTargetMonth);
    const dt = new Date(Date.UTC(targetY, targetM, day));
    const iso = `${dt.getUTCFullYear().toString().padStart(4, "0")}-${(
      dt.getUTCMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${dt
      .getUTCDate()
      .toString()
      .padStart(2, "0")}`;
    if (upperBound !== null && iso > upperBound) break;
    out.push(iso);
  }
  return out;
}
